import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../traderhub24-logo.svg";
import { useAuth } from "../contexts/AuthContext";
import { useMembership } from "../hooks/useMembership";
import { useMarketDataRefreshInterval } from "../hooks/useMarketDataRefreshInterval";
import { supabase } from "../lib/supabase";
import { getAdminAppUrl } from "../lib/adminApp";
import { useDashboardLayout } from "../contexts/DashboardLayoutContext";
import { DashboardSidebarProvider } from "../contexts/DashboardSidebarContext";
import { DRAGGABLE_WIDGET_IDS, DASHBOARD_WIDGETS, getBaseWidgetId } from "../config/dashboardWidgets";
import { navigateToTradeDetail } from "../utils/tradeNavigation";
import type { Trade } from "../types/trade";


interface DashboardLayoutProps {
  children: React.ReactNode;
  onAddTrade?: () => void;
  openTradesRefreshTrigger?: number;
  /** When true, main content area does not scroll - only inner sections (e.g. Feed, TradeList) scroll */
  noPageScroll?: boolean;
}

const NAV_ITEMS = [
  { id: "home", path: "/app", labelKey: "common:nav.dashboard", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  { id: "all-trades", path: "/app/all-trades", labelKey: "common:nav.allTrades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
  { id: "insights", path: "/app/insights", labelKey: "common:nav.insights", icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z", showNewBadge: true },
  { id: "analytics", path: "/app/analytics", labelKey: "common:nav.analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
  { id: "accounts", path: "/app/accounts", labelKey: "common:nav.accounts", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H5a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { id: "tools", path: "/app/tools", labelKey: "common:nav.tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
  { id: "strategies", path: "/app/strategies", labelKey: "common:nav.strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
  { id: "social-trade", path: "/app/social-trade", labelKey: "common:nav.socialTrade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
  { id: "community", path: "/app/community", labelKey: "common:nav.community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
] as const;

export default function DashboardLayout({ children, onAddTrade, openTradesRefreshTrigger, noPageScroll = false }: DashboardLayoutProps) {
  const { t } = useTranslation(["common", "trading", "dashboard"]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (noPageScroll) {
      document.documentElement.classList.add('dashboard-no-scroll');
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      return () => {
        document.documentElement.classList.remove('dashboard-no-scroll');
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.documentElement.style.overflow = '';
        document.documentElement.style.height = '';
      };
    }
  }, [noPageScroll]);
  const { user, userProfile, signOut } = useAuth();
  const { membershipType } = useMembership();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("dashboard-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("dashboard-sidebar-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  const [showAddMetricsDropdown, setShowAddMetricsDropdown] = useState(false);
  const addMetricsRef = useRef<HTMLDivElement>(null);
  const {
    isEditMode,
    enterEditMode,
    saveAndExit,
    addWidget,
    pendingEnabled,
  } = useDashboardLayout();
  const [openTrades, setOpenTrades] = useState<Trade[]>([]);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchOpenTrades = async () => {
      if (!user) {
        setOpenTrades([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("trades_with_market_data")
          .select("*")
          .eq("user_id", user.id)
          .is("exit_date", null)
          .order("entry_date", { ascending: false });
        if (error) throw error;
        setOpenTrades((data as Trade[]) || []);
      } catch (e) {
        console.error("Failed to fetch open trades:", e);
        setOpenTrades([]);
      }
    };
    fetchOpenTrades();
  }, [user, openTradesRefreshTrigger]);

  const refreshOpenTrades = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("trades_with_market_data")
        .select("*")
        .eq("user_id", user.id)
        .is("exit_date", null)
        .order("entry_date", { ascending: false });
      if (error) throw error;
      setOpenTrades((data as Trade[]) || []);
    } catch (e) {
      console.error("Failed to refresh open trades:", e);
    }
  }, [user]);

  useMarketDataRefreshInterval(refreshOpenTrades, Boolean(user));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addMetricsRef.current && !addMetricsRef.current.contains(e.target as Node)) {
        setShowAddMetricsDropdown(false);
      }
    };
    if (showAddMetricsDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showAddMetricsDropdown]);

  const handleNavigateToTrade = (trade: Trade) => {
    navigateToTradeDetail(trade, navigate, membershipType)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  const isActive = (path: string) => {
    if (path === "/app") return location.pathname === "/app" || location.pathname === "/app/";
    return location.pathname.startsWith(path);
  };

  const navButton = (path: string) => {
    navigate(path);
    setShowMobileSidebar(false);
  };

  const sidebarContent = (
    <>
      <div className="px-2 py-2 border-b border-gray-200 flex items-center justify-between md:block">
        <button
          onClick={() => {
            navigate("/app");
            setShowMobileSidebar(false);
          }}
          className="flex items-center focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded-lg p-0.5 -m-0.5"
        >
          <img src={logo} alt="TraderHub24" className="h-14 w-auto" />
        </button>
        <button
          onClick={() => setShowMobileSidebar(false)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          Menu
        </p>
        {NAV_ITEMS.map((item) => {
          const isDashboard = item.id === "home";
          const showDashboardEdit = isDashboard && user && (location.pathname === "/app" || location.pathname === "/app/");
          const hasTradeList = pendingEnabled.some((id) => getBaseWidgetId(id) === 'tradeList');
          const availableToAdd = DRAGGABLE_WIDGET_IDS.filter(
            (baseId) =>
              baseId !== 'tradeList' || !hasTradeList
          );
          return (
            <div key={item.id}>
              {showDashboardEdit && isEditMode && (
                <div className="flex gap-2 mb-2 px-1" ref={addMetricsRef}>
                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() => setShowAddMetricsDropdown((v) => !v)}
                      disabled={availableToAdd.length === 0}
                      className="w-full px-3 py-2 text-xs font-medium rounded-lg bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("dashboard:editLayout.addWidgets")}
                    </button>
                    {showAddMetricsDropdown && availableToAdd.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 w-full min-w-[140px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                        {availableToAdd.map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              addWidget(id);
                              setShowAddMetricsDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            {t(DASHBOARD_WIDGETS[id].labelKey)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => saveAndExit()}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                  >
                    {t("dashboard:editLayout.save")}
                  </button>
                </div>
              )}
              <div className="flex items-center gap-1">
                <div className="flex-1 relative">
                  <button
                    onClick={() => navButton(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? "bg-orange-50 text-orange-600 border border-orange-200"
                        : "text-gray-700 hover:bg-gray-100 border border-transparent"
                    }`}
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {t(item.labelKey)}
                  </button>
                  {"showNewBadge" in item && item.showNewBadge && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                      new
                    </span>
                  )}
                </div>
                {showDashboardEdit && !isEditMode && (
                  <button
                    type="button"
                    onClick={enterEditMode}
                    className="p-2 rounded-lg text-gray-500 hover:text-orange-600 hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-colors"
                    title={t("dashboard:editLayout.title")}
                    aria-label={t("dashboard:editLayout.title")}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div className="pt-4">
          <button
            onClick={() => {
              onAddTrade?.();
              setShowMobileSidebar(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t("common:nav.addTrade")}
          </button>
        </div>
      </nav>

      <div className="p-3 border-t border-gray-200">
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="truncate">{user?.email?.split("@")[0] ?? "Account"}</div>
              <div className="text-xs text-gray-500 truncate">
                {t("common:header.planLabel", { plan: membershipType ? membershipType.charAt(0).toUpperCase() + membershipType.slice(1) : "Free" })}
              </div>
            </div>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showUserMenu && (
            <div className="absolute bottom-full left-0 right-0 mb-1 py-1 bg-app-box rounded-xl shadow-lg border border-gray-200">
              {userProfile?.is_admin && (
                <button
                  onClick={() => {
                    window.open(getAdminAppUrl("/"), "_blank", "noopener,noreferrer");
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  </svg>
                  Admin Panel
                  <span className="ml-auto text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded">
                    {t("common:nav.adminBadge")}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  navigate("/app/account");
                  setShowUserMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                </svg>
                {t("common:header.accountSettings")}
              </button>
              <button
                onClick={() => {
                  signOut();
                  setShowUserMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t("common:header.signOut")}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={`bg-app-bg text-gray-900 flex ${noPageScroll ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-app-box border-b border-gray-200 flex items-center justify-between px-2 z-30">
        <button
          onClick={() => setShowMobileSidebar(true)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src={logo} alt="TraderHub24" className="h-10 w-auto" />
        <div className="w-10" />
      </div>

      {/* Mobile sidebar overlay */}
      {showMobileSidebar && (
        <div className="md:hidden fixed inset-0 z-[100] flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileSidebar(false)}
            aria-hidden="true"
          />
          <aside className="relative w-64 max-w-[85%] h-full bg-app-box shadow-xl flex flex-col animate-in slide-in-from-left duration-200">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Desktop Left Sidebar - collapsible */}
      <aside
        className={`hidden md:flex flex-shrink-0 bg-app-box border-r border-gray-200 flex-col fixed left-0 top-0 h-full z-40 transition-[width] duration-200 ease-in-out ${
          sidebarCollapsed ? "w-0 overflow-hidden border-0" : "w-64"
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Sidebar toggle - desktop only, edge of viewport */}
      <button
        type="button"
        onClick={toggleSidebar}
        className="hidden md:flex fixed left-0 top-1/2 -translate-y-1/2 z-50 w-6 h-12 bg-app-box border border-gray-200 rounded-r-lg shadow-md items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        style={{ left: sidebarCollapsed ? 0 : 256 }}
        aria-label={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
      >
        <svg className={`w-4 h-4 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Main content - tam genişlik, responsive; sağ boşluk yok */}
      <main
        className={`flex-1 min-w-0 min-h-0 pt-14 md:pt-0 pr-0 flex flex-col transition-[margin-left] duration-200 ${
          noPageScroll ? "overflow-hidden" : "overflow-auto"
        } ${sidebarCollapsed ? "md:pl-0" : "pl-0 md:pl-64"}`}
      >
        <div className={`w-full max-w-full flex flex-col ${noPageScroll ? 'flex-1 min-h-0 overflow-hidden' : 'flex-none min-h-fit'}`}>
        {/* Top slider - Open trades as boxes (pozisyon detayları) */}
        {openTrades.length > 0 && (
          <div className="w-full bg-app-box border-b border-gray-200 px-2 md:px-4 py-2 overflow-hidden">
            <div className="relative">
              <div 
                className="flex gap-2" 
                style={{ 
                  width: 'max-content',
                  animation: 'scroll-left 50s linear infinite'
                }}
              >
                {/* Duplicate items for seamless loop */}
                {[...openTrades, ...openTrades].map((t, index) => (
                  <button
                    key={`${t.id}-${index}`}
                    onClick={() => handleNavigateToTrade(t)}
                    className="flex-shrink-0 px-4 md:px-8 py-1 rounded-lg bg-white border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all flex flex-row items-center justify-center gap-1.5 md:gap-2 min-h-[36px] md:min-h-[38px]"
                  >
                    <div className="font-semibold text-xs md:text-sm text-gray-900 whitespace-nowrap">
                      {t.symbol}
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-500 whitespace-nowrap">
                      P/L
                    </div>
                    <div
                      className={`text-xs md:text-sm font-bold whitespace-nowrap ${
                        t.unrealized_pnl_percentage != null
                          ? t.unrealized_pnl_percentage >= 0
                            ? "text-green-600"
                            : "text-red-600"
                          : "text-gray-500"
                      }`}
                    >
                      {t.unrealized_pnl_percentage != null
                        ? `${t.unrealized_pnl_percentage >= 0 ? "+" : ""}${t.unrealized_pnl_percentage.toFixed(2)}%`
                        : "–"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        <DashboardSidebarProvider sidebarCollapsed={sidebarCollapsed}>
          {children}
        </DashboardSidebarProvider>
        </div>
      </main>
    </div>
  );
}
